import i18n from '@/shared/i18n';
import { getActiveScheduleByType, getProductTitle } from '@/shared/utils';
import { getFormatedLongDate, getFormatedMonthDateWithTime } from '@/index/shared/filters';
import dayjs from 'dayjs';

const parser = {
  actors(product) {
    if (!product.actors) {
      return;
    }

    return product.actors.map((actor) => actor.name);
  },
  audios(product) {
    if (!product.audioLanguages) {
      return;
    }

    return product.audioLanguages.map((audioLanguage) => audioLanguage.name);
  },
  category(product) {
    if (!product.mainCategory) {
      return;
    }

    return product.mainCategory.name;
  },
  directors(product) {
    if (!product.directors) {
      return;
    }

    return product.directors.map((director) => director.name);
  },
  duration(product) {
    if (!product.duration) {
      return;
    }

    const seconds = product.duration;
    const hours = Math.floor((seconds / 60) / 60);
    const minutes = Math.floor((seconds / 60) % 60);
    let result = '';

    if (seconds < 60) {
      result += `${seconds} sec`;
    }
    if (hours > 0) {
      result += `${hours} h`;
    }
    if (minutes > 0) {
      result += ` ${minutes} min`;
    }

    return result;
  },
  episode(product) {
    if (!product.episode) {
      return;
    }

    return `${i18n.t('glossary.episode')} ${product.episode}`;
  },
  seasonAndEpisode(product) {
    const season = String(product.season.number).padStart(2, '0');
    const episode = String(product.episode).padStart(2, '0');
    return `S${season}E${episode}`;
  },
  seasonFormat(product) {
    return `${i18n.t('glossary.season')}  ${String(product.season.number)}`;
  },
  numberOfSeasons(product) {
    if (!product.seasons.length) {
      return;
    }

    return `${product.seasons.length} ${i18n.tc('common.seasons', product.seasons.length)}`;
  },
  genres(product) {
    if (!product.genres) {
      return;
    }

    return product.genres.map((genre) => genre.name);
  },
  productTitle(product) {
    return product.title;
  },
  rating(product) {
    if (!product.rating) {
      return;
    }

    return product.rating + '+';
  },
  richTitle(product) {
    return getProductTitle(product, { richTitle: true });
  },
  sinceDate(product) {
    return getFormatedMonthDateWithTime(product.since);
  },
  availabilityRange(product) {
    return getFormatedMonthDateWithTime(product.since, product.till);
  },
  sinceTime(product) {
    const schedule = getActiveScheduleByType(product, 'NORMAL');
    if (!schedule) {
      return;
    }

    const since = new Date(schedule.since).getTime();
    const now = Date.now();
    const timeDifferenceInMin = (now - since) / 1000 / 60;
    const MINUTES_IN_HOUR = 60;
    const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
    const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
    const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365;

    const diff = timeDifferenceInMin;
    if (diff < 0) {
      return;
    }
    if (diff < 1) {
      return i18n.t('sinceTime.moment');
    }
    if (diff < 2) {
      return i18n.t('sinceTime.minute');
    }
    if (diff < MINUTES_IN_HOUR) {
      return i18n.t('sinceTime.minutes', { min: Math.floor(diff) });
    }
    if (diff < MINUTES_IN_HOUR * 2) {
      return i18n.t('sinceTime.hour');
    }
    if (diff < MINUTES_IN_DAY) {
      return i18n.t('sinceTime.hours', { hours: Math.floor(diff / MINUTES_IN_HOUR) });
    }
    if (diff < (MINUTES_IN_DAY * 2)) {
      return i18n.t('sinceTime.day');
    }
    if (diff < (MINUTES_IN_WEEK)) {
      return i18n.t('sinceTime.days', { days: Math.floor(diff / MINUTES_IN_DAY) });
    }
    if (diff < (MINUTES_IN_WEEK * 2)) {
      return i18n.t('sinceTime.week');
    }
    if (diff < (MINUTES_IN_DAY * 31)) {
      return i18n.t('sinceTime.weeks', { weeks: Math.floor(diff / MINUTES_IN_WEEK) });
    }
    if (diff < (MINUTES_IN_DAY * 61)) {
      return i18n.t('sinceTime.month');
    }
    if (diff < (MINUTES_IN_YEAR)) {
      return i18n.t('sinceTime.months', { months: dayjs(now).diff(dayjs(since), 'month') });
    }
    if (diff < (MINUTES_IN_YEAR * 2)) {
      return i18n.t('sinceTime.year');
    }
    if (diff >= (MINUTES_IN_YEAR * 2)) {
      return i18n.t('sinceTime.years', { years: new Date(now).getFullYear() - new Date(since).getFullYear() });
    }
  },
  sinceDateFormat(product) {
    return getFormatedLongDate(product.since);
  },
  seriesFormat(product) {
    if (!product.season || !product.season.serial) {
      return;
    }

    const seasonNumber = product.season.number;
    const serial = product.season.serial;
    let result = '';

    if (serial.showEpisodeNumber) {
      if (serial.showSeasonNumber && seasonNumber > 0) {
        result += `S${String(seasonNumber).padStart(2, '0')}`;
      }
      result += `E${String(product.episode).padStart(2, '0')}`;
    }

    return result;
  },
  seriesTitle(product) {
    if (!product.season || !product.season.serial) {
      return;
    }

    return product.season.serial.title;
  },
  subtitles(product) {
    if (!product.subtitleLanguages) {
      return;
    }

    return product.subtitleLanguages.map((subtitleLanguage) => subtitleLanguage.name);
  },
  availableFrom(product) {
    return `${i18n.t('glossary.availableFrom')} ${this.availabilityRange(product)}`;
  },
};

function parse(product, key, options) {
  let value = product[key];

  if (key in parser) {
    value = parser[key](product);
  }

  if (value instanceof Array) {
    return value
      .filter((val) => val)
      .slice(0, options.limit)
      .join(options.separator);
  }

  return value;
}

export default function meta(product, keys, opts = {}) {
  if (!product) {
    return;
  }

  const options = {
    divider: ' | ',
    separator: ', ',
    ...opts,
  };

  if (keys instanceof Array) {
    return keys
      .map((key) => parse(product, key, options))
      .filter((key) => key)
      .join(options.divider);
  }

  return parse(product, keys, options);
}
